import React, { FC, useEffect, useState } from 'react'
import {
  Field,
  FieldProps,
  Form,
  Formik,
  FormikProps,
  FormikTouched,
  getIn,
  setNestedObjectValues,
} from 'formik'
import {
  Button,
  Dropdown,
  Heading,
  Hr,
  Paragraph,
  SingleDateInput,
  TextArea,
} from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ContextNotification } from '@pandiafi/components'

import {
  Contract,
  ContractTenant,
  FeatureFlag,
  ModifyContractInput,
  modifyContractInputSchema,
  UserMetadata,
} from '../../../../common/validations'
import { TenantSelector } from '../components/TenantSelector/TenantSelector'
import FormFieldWrapper from '../../../components/FormField'
import {
  renderDate,
  safeISODateFromLocaleDate,
  validateFormValues,
} from '../../../utils'
import {
  getAllowedMonthEndDates,
  isAllowedMonthEndDateWithinRange,
} from '../../../utils/terminationDateUtils'
import { FORM_FIELD_VALIDATION_ERROR_CLASSNAME } from '../../../components/FormField/FormFieldConstants'
import { ModificationPreview } from './ModificationPreview'
import { ModificationContractSuccess } from './ModificationSuccess'
import { ResidentAppError } from '../../../../common/error'
import {
  ResidentAppErrorCode,
  TerminationDisallowedReason,
} from '../../../../common/types'
import { TerminationDisallowedBanner } from '../components/TerminationDisallowedBanner/TerminationDisallowedBanner'
import { TenantSelectionNotification } from './TenantSelectionNotification/TenantSelectionNotification'
import { useContractPaths } from '../../../hooks/useContractPaths/useContractPaths'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { Breakpoint } from 'components'

type Props = {
  contract: Contract
  tenants: ContractTenant[]
  terminateTenants: number[]
  setTerminateTenants: (value: number[]) => void
  tenantSelectionConfirmed: boolean
  setTenantSelectionConfirmed: (value: boolean) => void
  userMetadata: UserMetadata
  featureFlags?: FeatureFlag
}

type View = 'form' | 'preview' | 'success'

const ADD_PERSON_DISALLOWED_REASONS: TerminationDisallowedReason[] = [
  TerminationDisallowedReason.HAS_GUARDIAN_OF_INTERESTS,
  TerminationDisallowedReason.PENDING_MODIFICATION_REQUEST,
  TerminationDisallowedReason.PENDING_TERMINATION_TENANT_APPROVALS,
  TerminationDisallowedReason.PENDING_TERMINATION_REVIEW,
  TerminationDisallowedReason.CONTRACT_TERMINATED,
  TerminationDisallowedReason.NOT_CONTRACT_PARTY,
  TerminationDisallowedReason.OPEN_CONTINUATION_OFFER,
]

export const ModifyContractForm: FC<Props> = ({
  contract,
  tenants,
  terminateTenants,
  setTerminateTenants,
  tenantSelectionConfirmed,
  setTenantSelectionConfirmed,
  userMetadata,
  featureFlags,
}) => {
  const [params] = useSearchParams()
  const { t } = useTranslation([
    'common',
    'contractModification',
    'terminateContract',
  ])
  const [view, setView] = useState<View>('form')
  const navigate = useNavigate()
  const { addTenantPath } = useContractPaths()
  const [activeTermination, setActiveTermination] = useState(false)
  const moduleConfig = useModuleConfig()
  const endDateLastDayOfMonth =
    moduleConfig.terminateContract?.endDateLastDayOfMonth ?? true
  const now = DateTime.now()
  const inEffectDisabledBefore = now.startOf('day').toJSDate()
  const inEffectDisabledAfter = now.plus({ months: 2 })

  const terminationDisallowed = contract.terminationDisallowedReason

  useEffect(() => {
    // Show success view if the contract was successfully terminated
    if (params.get('success')) {
      setView('success')
    }

    // Throw backend errors
    const error = params.get('error')

    if (error) {
      // The error is handled in the ErrorHandler component
      throw ResidentAppError.ClientError(error as ResidentAppErrorCode)
    }
  }, [params, setView])

  useEffect(() => {
    setActiveTermination(
      hasActiveTermination(
        tenants,
        contract.isFixedTerm,
        contract.endDate ?? undefined
      )
    )
  }, [tenants, contract.isFixedTerm, contract.endDate])

  // returns true if at least one tenant’s contract ends earlier than the main contract’s end date
  const hasActiveTermination = (
    tenantList: { endDate?: string | Date | null }[],
    isFixedTerm?: boolean | null,
    contractEndDate?: string | Date
  ): boolean => {
    if (isFixedTerm && contractEndDate) {
      const normalizeDate = (date: string | Date) =>
        new Date(date).toISOString().split('T')[0] // YYYY-MM-DD
      const contractEnd = new Date(normalizeDate(contractEndDate))
      return tenantList.some((tenant) => {
        if (!tenant.endDate) return false
        const tenantEnd = new Date(normalizeDate(tenant.endDate))
        return tenantEnd < contractEnd
      })
    }
    return tenantList.some((tenant) => !!tenant.endDate)
  }

  const contractParties = tenants.filter((tenant) => tenant.isContractParty)
  const isSelectingTenants =
    terminateTenants.length > 0 && !tenantSelectionConfirmed
  const otherContractPartySelected = contractParties.some(
    (contractParty) =>
      contractParty.id !== contract.tenant.id &&
      terminateTenants.includes(contractParty.id)
  )
  const showTransferTenantField = contractParties.every((contractParty) =>
    terminateTenants.includes(contractParty.id)
  )
  const showAddPersonButton =
    (!terminationDisallowed ||
      !ADD_PERSON_DISALLOWED_REASONS.includes(terminationDisallowed)) &&
    !activeTermination

  const getPossibleNewContractParties = (terminateTenantIds: number[]) =>
    tenants
      .filter((tenant) => 'isAdult' in tenant)
      .filter((tenant) => !tenant.isContractParty && tenant.isAdult)
      .filter((tenant) => !terminateTenantIds.includes(tenant.id))

  const possibleNewContractPartiesOptions = getPossibleNewContractParties(
    terminateTenants
  ).map((party) => ({
    id: party.id,
    label: `${party.firstName} ${party.lastName}`,
  }))

  const inEffectMustBeMonthEnd =
    endDateLastDayOfMonth && showTransferTenantField
  const allowedDates = inEffectMustBeMonthEnd
    ? getAllowedMonthEndDates(now, inEffectDisabledAfter)
    : undefined

  const handleValidate = (form: ModifyContractInput) => {
    const allValidationErrors = validateFormValues(
      form,
      modifyContractInputSchema
    )

    if (
      inEffectMustBeMonthEnd &&
      !allValidationErrors.inEffect &&
      !isAllowedMonthEndDateWithinRange(
        form.inEffect,
        now,
        inEffectDisabledAfter,
        allowedDates
      )
    ) {
      allValidationErrors.inEffect = t(
        'common:datePicker.monthEndOnlyValidation'
      )
    }

    return allValidationErrors
  }

  const handlePreview = async (form: FormikProps<ModifyContractInput>) => {
    const errors = await form.validateForm()

    if (Object.keys(errors).length > 0) {
      // Mark fields with errors as touched to show error messages
      await form.setTouched(
        setNestedObjectValues<FormikTouched<ModifyContractInput>>(errors, true)
      )
      document
        .querySelector(`.${FORM_FIELD_VALIDATION_ERROR_CLASSNAME}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      window.scrollTo(0, 0)
      setView('preview')
    }
  }

  const initialValues: ModifyContractInput = {
    inEffect: now.endOf('month').toFormat('yyyy-MM-dd'),
    contractPartyTenantIds: contractParties.map((tenant) => tenant.id),
    possibleContractPartyTenants:
      getPossibleNewContractParties(terminateTenants),
    currentUserTenantId: contract.tenant.id,
    tenants,
    removeTenants: terminateTenants.map((tenantId) => ({ tenantId })),
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {}}
      validate={handleValidate}
    >
      {(form: FormikProps<ModifyContractInput>) => {
        const genericError: string | undefined = getIn(
          form.errors,
          'genericError'
        )

        return (
          <>
            {view === 'success' && <ModificationContractSuccess />}
            {view === 'preview' && (
              <ModificationPreview
                cancel={() => setView('form')}
                contract={contract}
                tenants={tenants}
                formValues={form.values}
                userMetadata={userMetadata}
              />
            )}
            {view === 'form' && (
              <Form>
                <Breakpoint show tablet desktop>
                  {initialValues.possibleContractPartyTenants.some(
                    (tenant) => tenant.id === contract.tenant.id
                  ) &&
                  activeTermination &&
                  contract.terminationDisallowedReason !==
                    TerminationDisallowedReason.CONTRACT_TERMINATED ? (
                    <ContextNotification
                      type="warning"
                      textAlign="center"
                      className="mb-6"
                    >
                      {t(
                        'terminateContract:terminationDisallowedReason.pendingContractParty'
                      )}
                    </ContextNotification>
                  ) : (
                    <TerminationDisallowedBanner
                      reason={terminationDisallowed}
                      contractType={contract.contractType}
                    />
                  )}
                </Breakpoint>

                {isSelectingTenants && (
                  <TenantSelectionNotification
                    otherContractPartySelected={otherContractPartySelected}
                    genericError={genericError}
                  />
                )}

                <Heading element="h3" size="l" className="phone:font-medium">
                  {t('contractModification:headings.parties')}
                </Heading>

                <TenantSelector
                  tenants={tenants}
                  contract={contract}
                  terminate={terminateTenants}
                  activeTermination={activeTermination}
                  handleTerminateChange={async (values) => {
                    setTerminateTenants(values)
                    await form.setFieldValue(
                      'possibleContractPartyTenants',
                      getPossibleNewContractParties(values),
                      false
                    )
                    await form.setFieldValue(
                      'removeTenants',
                      values.map((i) => ({ tenantId: i })),
                      false
                    )
                    await form.setFieldTouched(
                      'transfer.destinationTenantId',
                      true,
                      true
                    )
                    await form.validateForm()
                  }}
                  featureFlags={featureFlags}
                />

                {showAddPersonButton && (
                  <Button
                    variant="add"
                    icon="plus-circle"
                    className="mt-8"
                    disabled={terminateTenants.length > 0}
                    onClick={() => {
                      navigate(addTenantPath)
                    }}
                  >
                    {t('contractModification:buttons.addPerson')}
                  </Button>
                )}

                {isSelectingTenants && (
                  <div className="mt-8 flex items-center justify-end gap-4">
                    <Button
                      type="button"
                      variant="cancel"
                      onClick={() => {
                        window.scrollTo(0, 0)
                        form.resetForm()
                        setTerminateTenants([])
                        setTenantSelectionConfirmed(false)
                      }}
                    >
                      {t('common:cancel')}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={
                        otherContractPartySelected || Boolean(genericError)
                      }
                      onClick={async () => {
                        const errors = await form.validateForm()

                        if (!getIn(errors, 'genericError')) {
                          setTenantSelectionConfirmed(true)
                        }
                      }}
                    >
                      {t('common:next')}
                    </Button>
                  </div>
                )}

                {tenantSelectionConfirmed && terminateTenants.length > 0 && (
                  <>
                    <Hr className="my-10 border-common-gray-4" />

                    <Heading size="l" element="h3" className="mb-4 font-medium">
                      {t('contractModification:headings.tenantLeaves', {
                        count: terminateTenants.length,
                      })}
                    </Heading>

                    <Paragraph large className="whitespace-pre-line">
                      {t('contractModification:texts.tenantLeavingDescription')}
                    </Paragraph>

                    <Field
                      name="inEffect"
                      validate={(value: string) => {
                        if (!inEffectMustBeMonthEnd) {
                          return undefined
                        }

                        return isAllowedMonthEndDateWithinRange(
                          value,
                          now,
                          inEffectDisabledAfter,
                          allowedDates
                        )
                          ? undefined
                          : t('common:datePicker.monthEndOnlyValidation')
                      }}
                    >
                      {({
                        field,
                        meta,
                      }: FieldProps<ModifyContractInput['inEffect']>) => (
                        <FormFieldWrapper
                          name={field.name}
                          label={t('contractModification:labels.movingOutDate')}
                          className="min-tablet:max-w-60 mt-8"
                          meta={meta}
                          labelBold
                          required
                          description={
                            inEffectMustBeMonthEnd
                              ? t('common:datePicker.monthEndOnly')
                              : undefined
                          }
                        >
                          <SingleDateInput
                            {...field}
                            value={renderDate(field.value)}
                            onChange={async (value) => {
                              const date = safeISODateFromLocaleDate(value)

                              if (date) {
                                await form.setFieldValue(field.name, date, true)
                              } else {
                                form.setFieldValue(field.name, value, false)
                              }

                              await form.setFieldTouched(field.name, true)
                            }}
                            allowedDates={allowedDates}
                            disabledAfter={inEffectDisabledAfter.toJSDate()}
                            disabledBefore={inEffectDisabledBefore}
                            placeholder={t('common:placeholders.date')}
                            datePickerPosition="bottomLeft"
                          />
                        </FormFieldWrapper>
                      )}
                    </Field>

                    {showTransferTenantField && (
                      <Field name="transfer.destinationTenantId">
                        {({
                          field,
                          meta,
                        }: FieldProps<
                          NonNullable<
                            ModifyContractInput['transfer']
                          >['destinationTenantId']
                        >) => (
                          <FormFieldWrapper
                            name={field.name}
                            label={t(
                              'contractModification:labels.transferDestination'
                            )}
                            className="min-tablet:max-w-96 mt-8"
                            meta={meta}
                            labelBold
                            required
                          >
                            <Dropdown
                              options={possibleNewContractPartiesOptions}
                              onChange={async (option) => {
                                if (option) {
                                  await form.setFieldValue(
                                    field.name,
                                    option.id,
                                    false
                                  )
                                  await form.setFieldValue(
                                    'transfer.sourceTenantId',
                                    contractParties[0].id
                                  )
                                } else {
                                  await form.setFieldValue(
                                    'transfer',
                                    undefined
                                  )
                                }
                                await form.setFieldTouched(
                                  'transfer.destinationTenantId',
                                  true
                                )
                              }}
                              value={possibleNewContractPartiesOptions.find(
                                (party) => party.id === meta.value
                              )}
                              allowEmpty
                            />
                          </FormFieldWrapper>
                        )}
                      </Field>
                    )}

                    <Field name="description">
                      {({
                        field,
                        meta,
                      }: FieldProps<ModifyContractInput['description']>) => (
                        <FormFieldWrapper
                          name={field.name}
                          label={t(
                            'contractModification:labels.modificationDescription'
                          )}
                          className="min-tablet:flex-1 phone:mt-8 min-tablet:mt-8"
                          meta={meta}
                          labelBold
                        >
                          <TextArea
                            onChange={(e) =>
                              form.setFieldValue(field.name, e.target.value)
                            }
                            placeholder={t(
                              'contractModification:placeholders.modificationDescription'
                            )}
                            value={meta.value}
                          />
                        </FormFieldWrapper>
                      )}
                    </Field>

                    <div className="mt-16 flex items-center justify-end">
                      <Button
                        type="button"
                        variant="cancel"
                        onClick={() => {
                          window.scrollTo(0, 0)
                          form.resetForm()
                          setTerminateTenants([])
                          setTenantSelectionConfirmed(false)
                        }}
                      >
                        {t('common:cancel')}
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => handlePreview(form)}
                        disabled={form.isSubmitting || !form.isValid}
                      >
                        {t('common:preview')}
                      </Button>
                    </div>
                  </>
                )}
              </Form>
            )}
          </>
        )
      }}
    </Formik>
  )
}
